import { useEffect, useRef, useState } from 'react'
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import './claude-help.css'

// Claude Help — an admin/power-gated assistant that answers questions about the
// reports data, draws basic charts and exports result tables. Chat goes to
// /api/claude-help (read-only over the claude.* views); admins can propose
// management commentary which is applied via /api/claude-help/execute.

interface ChartSeries { key: string; label: string }
interface ChartSpec {
  chartType: 'bar' | 'line' | 'area' | 'pie'
  title: string
  xKey: string
  series: ChartSeries[]
  stacked: boolean
  data: Record<string, unknown>[]
}
interface Dataset {
  tool: string
  purpose: string | null
  columns: string[]
  rowCount: number
  truncated: boolean
  rows: Record<string, unknown>[]
}
interface PendingAction { type: string; summary: string; payload: Record<string, unknown> }
interface ChatResponse {
  reply: string
  toolsUsed: string[]
  charts: ChartSpec[]
  datasets: Dataset[]
  pendingActions?: PendingAction[]
}
interface ChatMsg {
  role: 'user' | 'assistant'
  content: string
  charts?: ChartSpec[]
  datasets?: Dataset[]
  toolsUsed?: string[]
  pending?: PendingAction[]
}

type ActionStatus = 'idle' | 'applying' | 'applied' | 'dismissed' | 'error'

const COLORS = ['#1f6f54', '#c66a1e', '#3b6ea5', '#8a4f9e', '#b0413e', '#557a2f', '#7a6d55', '#2f6f6f']

const STARTERS = [
  'How is the current season tracking on net revenue vs last season?',
  'Show me net revenue by department for this season as a chart.',
  'What were the top 5 booking sources by revenue this season?',
  'Plot monthly footfall for the current season.',
]

function toCsv(ds: Dataset): string {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const head = ds.columns.join(',')
  const body = ds.rows.map((r) => ds.columns.map((c) => esc(r[c])).join(',')).join('\n')
  return `${head}\n${body}`
}

function download(filename: string, text: string, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([text], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function ChartView({ spec }: { spec: ChartSpec }) {
  const { chartType, xKey, series, data, stacked } = spec
  if (chartType === 'pie') {
    const valueKey = series[0]?.key
    if (!valueKey) return null
    return (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={data} dataKey={valueKey} nameKey={xKey} outerRadius={110} label>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    )
  }
  if (chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip />
          <Legend />
          {series.map((s, i) => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={COLORS[i % COLORS.length]} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    )
  }
  if (chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip />
          <Legend />
          {series.map((s, i) => (
            <Area key={s.key} type="monotone" dataKey={s.key} name={s.label}
              stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.25}
              stackId={stacked ? 'a' : undefined} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    )
  }
  // bar (default)
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip />
        <Legend />
        {series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.label} fill={COLORS[i % COLORS.length]} stackId={stacked ? 'a' : undefined} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

function DataTable({ ds, idx }: { ds: Dataset; idx: number }) {
  const preview = ds.rows.slice(0, 8)
  return (
    <div className="ch-dataset">
      <div className="ch-dataset__head">
        <span className="ch-dataset__meta">
          {ds.rowCount.toLocaleString('en-GB')} row{ds.rowCount === 1 ? '' : 's'}
          {ds.truncated ? ' (capped)' : ''}
          {ds.purpose ? ` · ${ds.purpose}` : ''}
        </span>
        <button
          type="button"
          className="ch-btn ch-btn--small"
          onClick={() => download(`claude-help-${idx + 1}.csv`, toCsv(ds))}
        >
          Download CSV
        </button>
      </div>
      {preview.length > 0 && (
        <div className="ch-tablewrap">
          <table className="ch-table">
            <thead><tr>{ds.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
            <tbody>
              {preview.map((r, ri) => (
                <tr key={ri}>{ds.columns.map((c) => <td key={c}>{fmtCell(r[c])}</td>)}</tr>
              ))}
            </tbody>
          </table>
          {ds.rowCount > preview.length && (
            <div className="ch-table__more">…and {(ds.rowCount - preview.length).toLocaleString('en-GB')} more — download for the full set.</div>
          )}
        </div>
      )}
    </div>
  )
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'number') return v.toLocaleString('en-GB', { maximumFractionDigits: 2 })
  return String(v)
}

export function ClaudeHelp({ me }: { me: { isAdmin: boolean; isPower: boolean; isBudget: boolean; isEngagement: boolean } }) {
  const [messages, setMessages] = useState<ChatMsg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionStatus, setActionStatus] = useState<Record<string, ActionStatus>>({})
  const scrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, loading])

  const canUse = me.isAdmin || me.isPower || me.isBudget

  async function send(text: string) {
    const q = text.trim()
    if (!q || loading) return
    setError(null)
    const nextMsgs: ChatMsg[] = [...messages, { role: 'user', content: q }]
    setMessages(nextMsgs)
    setInput('')
    setLoading(true)
    try {
      const res = await fetch('/api/claude-help', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMsgs.map((m) => ({ role: m.role, content: m.content })) }),
      })
      if (!res.ok) {
        const detail = await res.json().catch(() => null)
        throw new Error(detail?.error || `${res.status} ${res.statusText}`)
      }
      const data = (await res.json()) as ChatResponse
      setMessages((cur) => [...cur, {
        role: 'assistant',
        content: data.reply,
        charts: data.charts,
        datasets: data.datasets,
        toolsUsed: data.toolsUsed,
        pending: data.pendingActions,
      }])
    } catch (e) {
      setError(String((e as Error)?.message ?? e))
      // Roll the question back into the input so it isn't lost.
      setMessages((cur) => cur.slice(0, -1))
      setInput(q)
    } finally {
      setLoading(false)
    }
  }

  async function applyAction(msgIdx: number, actIdx: number, action: PendingAction) {
    const key = `${msgIdx}:${actIdx}`
    setActionStatus((s) => ({ ...s, [key]: 'applying' }))
    try {
      const res = await fetch('/api/claude-help/execute', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actions: [{ type: action.type, payload: action.payload }] }),
      })
      const data = await res.json().catch(() => null)
      const r = data?.results?.[0]
      if (!res.ok || !r?.ok) throw new Error(r?.error || `${res.status}`)
      setActionStatus((s) => ({ ...s, [key]: 'applied' }))
      setMessages((cur) => [...cur, { role: 'assistant', content: `✓ Saved: ${r.summary || action.summary}` }])
    } catch (e) {
      setActionStatus((s) => ({ ...s, [key]: 'error' }))
      setError(`Couldn't apply: ${String((e as Error)?.message ?? e)}`)
    }
  }

  function dismissAction(msgIdx: number, actIdx: number) {
    setActionStatus((s) => ({ ...s, [`${msgIdx}:${actIdx}`]: 'dismissed' }))
  }

  if (!canUse) {
    return (
      <main className="app-main">
        <div className="placeholder">
          <strong>Claude Help</strong>
          <p>This assistant is available to Reports admins, power users and budget users. Ask an administrator for access.</p>
        </div>
      </main>
    )
  }

  return (
    <main className="app-main ch-main">
      <div className="ch-head">
        <div>
          <p className="ch-sub">Ask about the reports data, get charts, and export tables. Read-only.</p>
        </div>
        {messages.length > 0 && (
          <button type="button" className="ch-btn ch-btn--ghost" onClick={() => { setMessages([]); setError(null); setActionStatus({}) }}>
            New chat
          </button>
        )}
      </div>

      <div className="ch-scroll" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="ch-empty">
            <p className="ch-empty__lead">What would you like to know?</p>
            <div className="ch-starters">
              {STARTERS.map((s) => (
                <button key={s} type="button" className="ch-starter" onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`ch-msg ch-msg--${m.role}`}>
            <div className="ch-bubble">
              <div className="ch-text">{m.content}</div>

              {m.charts?.map((c, ci) => (
                <div className="ch-chart" key={ci}>
                  <div className="ch-chart__title">{c.title}</div>
                  <ChartView spec={c} />
                </div>
              ))}

              {m.datasets?.map((d, di) => <DataTable key={di} ds={d} idx={di} />)}

              {m.pending?.map((a, ai) => {
                const st = actionStatus[`${i}:${ai}`] ?? 'idle'
                return (
                  <div key={ai} className={`ch-confirm ch-confirm--${st}`}>
                    <div className="ch-confirm__summary">{a.summary}</div>
                    {st === 'applied' ? (
                      <div className="ch-confirm__done">✓ Saved</div>
                    ) : st === 'dismissed' ? (
                      <div className="ch-confirm__dismissed">Dismissed</div>
                    ) : (
                      <div className="ch-confirm__actions">
                        <button type="button" className="ch-btn ch-btn--primary" disabled={st === 'applying'}
                          onClick={() => applyAction(i, ai, a)}>
                          {st === 'applying' ? 'Applying…' : 'Apply'}
                        </button>
                        <button type="button" className="ch-btn ch-btn--ghost" disabled={st === 'applying'}
                          onClick={() => dismissAction(i, ai)}>
                          Dismiss
                        </button>
                        {st === 'error' && <span className="ch-confirm__err">failed — try again</span>}
                      </div>
                    )}
                  </div>
                )
              })}

              {m.role === 'assistant' && m.toolsUsed && m.toolsUsed.length > 0 && (
                <div className="ch-trace">looked at: {Array.from(new Set(m.toolsUsed)).join(', ')}</div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="ch-msg ch-msg--assistant">
            <div className="ch-bubble ch-bubble--thinking"><span className="ch-dot" /><span className="ch-dot" /><span className="ch-dot" /></div>
          </div>
        )}
      </div>

      {error && <div className="ch-error">{error}</div>}

      <form className="ch-inputbar" onSubmit={(e) => { e.preventDefault(); send(input) }}>
        <textarea
          className="ch-input"
          value={input}
          placeholder="Ask about revenue, footfall, budget, Pullman, membership…"
          rows={1}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input) }
          }}
          disabled={loading}
        />
        <button type="submit" className="ch-btn ch-btn--primary" disabled={loading || !input.trim()}>Send</button>
      </form>
      <p className="ch-caveat">Claude can make mistakes — sense-check figures before relying on them. Data refreshes roughly every 2 hours.</p>
    </main>
  )
}
